import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from "styled-components";
import Dialog from "@shared/ui/molecules/Dialog";
import { useDispatch, useSelector } from "react-redux";
import { closeDialog } from '@shared/store/dialog.duck';
import { useTranslation } from "react-i18next";
import { Moment } from "moment";
import { OutlineButton } from "@shared/ui/atoms/buttons/OutlineButton";
import { BodyLarge, BodyRegular, BodySmall, BodyVerySmall } from "@shared/ui/atoms/fonts/Body";
import { selectRecentDayNotes } from '@features/whereabouts/store/dayNotes.duck';
import { FreeBusyStatus, RecentDayNotes } from '@features/whereabouts/services/WhereaboutOptions';
import { Pill } from "@shared/ui/atoms/Pill";
import Colours from "@shared/ui/atoms/Colours";
import { warningNotification } from '@shared/store/notification.duck';
import { iconPackPath } from '@shared/utils/WhereaboutsHelper';
import { selectIconPack } from '@shared/store/auth.duck';
import { smallTablet } from "@shared/ui/atoms/MediaQueries";

export function DayNotesEntryDialog(props: Props) {
  const dispatch = useDispatch();
  const textRef = useRef<any>(null);
  const {t} = useTranslation();
  const recentDayNotes = useSelector(selectRecentDayNotes);
  const [note, setNote] = useState('');
  const iconPack = useSelector(selectIconPack);

  const close = () => {
    dispatch(closeDialog());
  }

  const filteredDayNotes = useMemo(() => {
    const limitedRecentDayNotes = recentDayNotes.filter((rdn: RecentDayNotes) => rdn.whereaboutsOptionId === props.payload.whereabouts.id);
    if (limitedRecentDayNotes.length > 3) {
      return limitedRecentDayNotes.slice(0, 3);
    }
    return limitedRecentDayNotes;
  }, [props.payload.whereabouts.id, recentDayNotes])

  useEffect(() => {
    textRef.current.onChangeText = (val: string) => console.log(val)
  }, []);

  const onSaveClicked = (e: Event) => {
    if (textRef.current.value.length > 100) {
      dispatch(warningNotification('Day note limit 100 characters'))
    } else {
      props?.payload?.onSaveClicked(e, textRef.current.value);
      close();
    }
  }

  const onQuickNoteSelected = (e: Event, quickNote: RecentDayNotes) => {
    props?.payload?.onSaveClicked(e, quickNote.note);
    close();
  }

  const onTextChange = (val: string) => {
    if (val.length < 100) {
      setNote(val);
    } else {
      setNote(val.substring(0, 100));
    }
  }

  return (
    <DialogWrapper isOpen={true} onClose={close}>
      <Header>
        <WhereaboutsHeaderImage src={iconPackPath(iconPack, props?.payload?.whereabouts.image, FreeBusyStatus.FREE)} />
        <BodyLarge weight={600}>{props?.payload?.whereabouts.label}</BodyLarge>
      </Header>
      <Content>
        <BodySmall>{t('day-notes-dialog.add-note')}</BodySmall>
        <TextArea ref={textRef} defaultValue={''} value={note} onChange={(e) => onTextChange(e.target.value)} />
        <BodyVerySmall style={{textAlign: 'right'}} colour={Colours.darkGrey}>{100-note.length} / 100</BodyVerySmall>
        {filteredDayNotes.length > 0 && <>
          <BodyRegular weight={600}>{t('day-notes-dialog.recent-notes')}</BodyRegular>
          <RecentNotesRow>
            {filteredDayNotes.map((dayNote: RecentDayNotes, key: number) => (<>
                <QuickSelectPill key={key} onClick={(e: Event) => onQuickNoteSelected(e, dayNote)}>
                  <BodySmall>{dayNote.note}</BodySmall>
                </QuickSelectPill>
              </>
            ))}
          </RecentNotesRow>
        </>}
        <OutlineButton style={{margin: '24px auto 0 auto'}}
                       text={'button.save'}
                       click={onSaveClicked}
                       fullWidth={true} />
      </Content>
    </DialogWrapper>
  )
}

interface Props {
  payload: {
    date: Moment
    whereabouts?: any;
    onSaveClicked: (e: Event, note: string) => any;
  };
}

const DialogWrapper = styled(Dialog)<any>`
  &.dialog {
    width: 100vw;
    height: 100vh;
    padding: 20px 0 0 0;
    @media (${smallTablet}) {
      padding: 20px 24px 24px 24px;
      max-width: 100%;
      min-height: 400px;
      width: unset;
      height: unset;
    }
  }
  .dialog__contentContainer, .dialog__content {
    width: 100%;
    padding-top: 0;
    @media (${smallTablet}) {
      width: 400px;
      min-height: 410px;
    }
  }
  .dialog__content {
    display: flex;
    flex-direction: column;
  }
`

const QuickSelectPill = styled(Pill)<any>`
  cursor: pointer;
  margin-right: 8px;
  &:hover {
    background-color: ${Colours.lightGrey};
  }
`

const RecentNotesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
`

export const Header = styled.div`
  border-bottom: 1px solid ${Colours.lightGrey};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: relative;
  min-height: 60px;
  margin-bottom: 24px;
`

export const Content = styled.div`
  max-width: 100%;
`

const WhereaboutsHeaderImage = styled.img`
  position: absolute;
  left: 0;
`

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  margin-bottom: 2px;
  padding: 4px 8px;
  font-size: 16px;
  font-family: "soleil", sans-serif;
  white-space: pre-wrap;
`
